import { useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { animate } from 'motion';

const PACK_SPRING = { type: 'spring' as const, stiffness: 380, damping: 32 };

function setCollapsed(panel: HTMLDivElement, inner: HTMLDivElement) {
  panel.style.height = '0px';
  panel.style.overflow = 'hidden';
  inner.style.opacity = '0';
  inner.style.transform = 'translateY(8px)';
}

function setExpanded(panel: HTMLDivElement, inner: HTMLDivElement) {
  panel.style.height = 'auto';
  panel.style.overflow = 'visible';
  inner.style.opacity = '1';
  inner.style.transform = 'none';
}

/**
 * Animated pack panel. Children unmount when collapsed so row players
 * do not stay subscribed while hidden.
 */
export function FonotekaCollapsible({
  open,
  children,
}: {
  open: boolean;
  children: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(open);
  const wasOpenRef = useRef(open);

  useLayoutEffect(() => {
    if (open) setMounted(true);
  }, [open]);

  useLayoutEffect(() => {
    if (!mounted) return;
    const panel = panelRef.current;
    const inner = innerRef.current;
    if (!panel || !inner) return;

    const wasOpen = wasOpenRef.current;
    wasOpenRef.current = open;

    let cancelled = false;
    const running: Array<{ stop: () => void; finished: Promise<unknown> }> = [];

    const run = async () => {
      if (open && !wasOpen) {
        setCollapsed(panel, inner);
        const toHeight = inner.scrollHeight;
        panel.style.overflow = 'hidden';
        const heightAnim = animate(panel, { height: ['0px', `${toHeight}px`] }, PACK_SPRING);
        const innerAnim = animate(inner, { opacity: 1, y: 0 }, PACK_SPRING);
        running.push(heightAnim, innerAnim);
        await Promise.all([heightAnim.finished, innerAnim.finished]);
        if (cancelled) return;
        setExpanded(panel, inner);
        return;
      }

      if (!open && wasOpen) {
        const fromHeight = panel.getBoundingClientRect().height;
        panel.style.overflow = 'hidden';
        const heightAnim = animate(panel, { height: [`${fromHeight}px`, '0px'] }, PACK_SPRING);
        const innerAnim = animate(inner, { opacity: 0, y: 8 }, PACK_SPRING);
        running.push(heightAnim, innerAnim);
        await Promise.all([heightAnim.finished, innerAnim.finished]);
        if (cancelled) return;
        setCollapsed(panel, inner);
        setMounted(false);
        return;
      }

      if (open) setExpanded(panel, inner);
      else setCollapsed(panel, inner);
    };

    void run();

    return () => {
      cancelled = true;
      for (const animation of running) animation.stop();
    };
  }, [open, mounted]);

  if (!mounted) {
    return <div className="fonoteka-pack-panel" style={{ height: 0, overflow: 'hidden' }} aria-hidden />;
  }

  return (
    <div ref={panelRef} className="fonoteka-pack-panel" aria-hidden={!open}>
      <div ref={innerRef} className="fonoteka-pack-table">
        {children}
      </div>
    </div>
  );
}
